// ---------------------------------------------------------------------------
// ModelingProgressBandV3
// ---------------------------------------------------------------------------
//
// Faixa unificada que substitui:
//   1. ``topCountersRow()`` da view ``InputsV3.js`` (os 3 cards do topo).
//   2. Chip de status do mês no ``monthlyStatusDiv`` + botão do drawer.
//
// Prop ``embedded``: sem Card próprio — encaixa no mesmo Card do seletor
// de meses na vista detalhada.

// Estados:
//   - ``compacto`` (padrao): uma linha com titulo, status do mes, botao
//     do drawer de progresso, barra horizontal de 4 segmentos clicaveis
//     e contador X/Y. Mais ou menos a mesma altura da toolbar de busca
//     + ordenacao que vem logo abaixo.
//   - ``expandido`` (toggle via chevron): replica os 3 cards classicos
//     com donut + tiles empilhados para filtro (Todos / Seus) e a
//     lista clicavel de responsaveis (terceiro card).
//
// Distincao de OK:
//   O codigo respeita a diferenciacao entre OK definitivo
//   (``delivered_ok``, verde escuro) e OK provisorio (``in_progress``,
//   verde claro, para inputs cujas checagens ainda nao terminaram).
//   Visualmente:
//     - barra do compacto: dois sub-segmentos contiguos dentro do
//       bucket "verde" (claro a esquerda, escuro a direita);
//     - donut do expandido: usa ``DashedDonut`` que ja preserva as
//       duas tonalidades de verde via paleta canonica;
//     - tile "Entregues": mostra dois pontinhos (claro+escuro)
//       sinalizando que o contador agrega os dois estados.
//
// Filtros:
//   Cada bucket clicavel da barra e cada tile sao espelhos dos chips
//   pre-existentes (``handleBadgeClick(scope, type, count)``) — a
//   logica de filtragem da lista de inputs continua em ``InputsV3``.
// ---------------------------------------------------------------------------

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";

import ExpandMore from "@material-ui/icons/ExpandMore";

import { useTranslation } from "react-i18next";

import Card from "../../../../components/Card/Card.js";
import CardBody from "../../../../components/Card/CardBody.js";
import GridContainer from "../../../../components/Grid/GridContainer.js";
import GridItem from "../../../../components/Grid/GridItem.js";
import useThemeColors from "../../../../hooks/useThemeColors";
import {
  dangerColor,
  grayColor,
  successColor,
  warningColor,
  whiteColor,
} from "../../../../assets/jss/material-dashboard-pro-react";

import DashedDonut from "../DashedDonut";
import useNativeWheelScroll from "../hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "../styles";
import {
  INPUT_STATE_BACKGROUNDS,
  inputStateLabel,
} from "../../../../services/inputsV3StateRules";
import { InputsLegendPill } from "../InputsLegend";

import useBandStyles from "./styles";

// ---------------------------------------------------------------------------
// Aglutinacao 9 -> 4 (tiles do expandido).
// ---------------------------------------------------------------------------
//
// A BARRA (compacta) usa as 9 cores canonicas direto de
// ``INPUT_STATE_BACKGROUNDS`` — uma celula por input, sem buckets. Esta
// tabela existe para o agrupamento usado APENAS nos 4 tiles do
// expandido (OK / Em atencao / Critico / Nao enviado). Mantem o numero
// de filtros baixo sem perder o vinculo com o estado canonico.
//
// Ordem dentro de cada bucket = "mais grave para menos grave"; usada
// tanto na ordem de renderizacao das cells da barra (do mais critico
// para o mais resolvido) quanto na decomposicao dos sublabels.
const STATE_TO_BUCKET = {
  // OK (verde): definitivos + provisorios aguardando avancar.
  delivered_ok: "delivered",
  in_progress: "delivered",
  // Atencao (amarelo): warning consolidado + parcial com sinal amarelo.
  attention: "attention",
  in_progress_attention: "attention",
  // Critico (vermelho): rejeitado + pergunta + falha tecnica.
  critical: "critical",
  pending_decision: "critical",
  system_error: "critical",
  // Nao enviado (cinza): sem dado + parser em andamento.
  not_sent: "not_sent",
  processing: "not_sent",
};

// Ordem dos 9 estados na barra (esquerda -> direita). Segue o gradiente
// "verde -> amarelo -> vermelho -> cinza" pedido pelo consultor: comeca
// pelo mais resolvido e termina pelo "sem dado / em processamento".
// Dentro de cada familia mantemos uma sub-ordem coerente:
//   verde   : definitivo -> provisorio
//   amarelo : parcial    -> consolidado
//   vermelho: pergunta   -> rejeitado -> falha tecnica
//   cinza   : sem dado   -> em parser
//
// Observacao: a ordem NAO casa mais com ``DashedDonut.DASH_SEVERITY``
// (que continua sendo "mais critico primeiro" para que o arco do donut
// destaque problemas no topo). Sao dois eixos visuais diferentes —
// barra horizontal "progresso/saude geral" vs donut "concentracao de
// problemas".
const BAR_STATE_ORDER = [
  "delivered_ok",
  "in_progress",
  "in_progress_attention",
  "attention",
  "pending_decision",
  "critical",
  "system_error",
  "not_sent",
  "processing",
];

// ---------------------------------------------------------------------------
// Persistencia local do estado expandido (sobrevive a refresh da view
// mas e' por usuario/navegador). Chave dedicada para evitar colisao
// com outros toggles da pagina.
// ---------------------------------------------------------------------------
const STORAGE_KEY = "inputs_v3.modeling_progress_band.expanded";

function readInitialExpanded() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "1") return true;
    if (raw === "0") return false;
  } catch (e) {
    /* localStorage indisponivel — ignora */
  }
  // Padrao: compacto (a faixa nasce dobrada para economizar vertical).
  return false;
}

function persistExpanded(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
  } catch (e) {
    /* ignora */
  }
}

// ---------------------------------------------------------------------------
// Helpers locais
// ---------------------------------------------------------------------------
//
// PA-022 (mai/2026): ``renderMonthChipIcon`` foi removido junto com
// a migracao do chip de status para a ``InputsV3GlobalTopBar`` —
// onde o estado do mes agora vive como dot + frase, sem chip.

// Decompoe o ``states`` (winner-take-all) devolvido por
// ``computeCounters`` em duas visoes:
//
//   - ``sub``: contagem CRUA por estado canonico (1 entrada por estado
//     conhecido — usada pela barra para renderizar 1 celula por input
//     com a cor canonica do pip).
//   - ``buckets``: aglutinacao 9 -> 4 (delivered / attention / critical
//     / not_sent) usada pelos tiles do expandido para preservar a
//     leitura "4 camadas de criticidade".
//
// Estados desconhecidos sao agregados como ``unknown`` (defensivo) —
// evita perder inputs da contagem total como aconteceu pos-introducao
// dos buckets ``in_progress_attention`` e ``system_error``, que eram
// silenciosamente descartados pelo classificador antigo.
function tallyStates(states) {
  const sub = {};
  let total = 0;
  let unknown = 0;
  (states || []).forEach((s) => {
    total += 1;
    if (!STATE_TO_BUCKET[s]) {
      unknown += 1;
      return;
    }
    sub[s] = (sub[s] || 0) + 1;
  });
  const buckets = {
    delivered: 0,
    attention: 0,
    critical: 0,
    not_sent: 0,
  };
  Object.entries(sub).forEach(([state, count]) => {
    buckets[STATE_TO_BUCKET[state]] += count;
  });
  return { sub, buckets, total, unknown };
}

// ---------------------------------------------------------------------------
// ProgressBar4 — barra "quadradinhos" (cada input = 1 cell)
// ---------------------------------------------------------------------------
//
// Visual analogo ao ``DashedDonut``: cada input do mes vira UM
// quadradinho colorido pelo seu estado canonico. O gap de 2px entre
// as cells deixa transparecer o fundo branco do trilho, separando
// visualmente cada pecinha.
//
// A barra e' puramente INFORMATIVA — o clique de filtro foi removido
// (vive apenas nos tiles do modo expandido). Mantemos tooltips por
// grupo de cor, encostados na barra via ``PopperProps``.

const BAR_TOOLTIP_POPPER_PROPS = {
  modifiers: {
    offset: { enabled: true, offset: "0, -10" },
  },
};

function ProgressBar4({ classes, counters }) {
  const { t } = useTranslation();
  const { sub, total } = tallyStates(counters.states);

  // Se o mes nao tem nenhum input ainda, a barra vira so o trilho
  // branco de fundo (sem cells). Evita largura zero quebrando o
  // layout.
  if (total === 0) {
    return <div className={classes.bar} aria-hidden="true" />;
  }

  // Uma celula por input, na ordem canonica ``BAR_STATE_ORDER`` (do
  // mais critico para o mais resolvido). Cor vem direto de
  // ``INPUT_STATE_BACKGROUNDS`` — mesma fonte usada pelo pip do
  // ``InputBoxV3``: assim a barra e os cards detalhados ficam
  // garantidamente em sincronia (pending_decision hachurado,
  // system_error preto, in_progress_attention laranja claro, etc.).
  //
  // Agrupamos as cells DO MESMO estado dentro de um <Tooltip> unico
  // para evitar 1 tooltip por celula (ruido com 50+ inputs); cada
  // grupo herda o label canonico do estado.
  const groups = BAR_STATE_ORDER.filter((s) => (sub[s] || 0) > 0).map((s) => ({
    state: s,
    count: sub[s],
    background: INPUT_STATE_BACKGROUNDS[s],
    label: `${sub[s]} · ${inputStateLabel(s, t) || s}`,
  }));

  return (
    <div className={classes.bar}>
      {groups.map((g) => (
        <Tooltip
          key={g.state}
          title={g.label}
          placement="top"
          PopperProps={BAR_TOOLTIP_POPPER_PROPS}
        >
          <div className={classes.barGroup} style={{ flex: g.count }}>
            {Array.from({ length: g.count }).map((_, idx) => (
              <div
                key={idx}
                className={classes.barCell}
                style={{ background: g.background }}
              />
            ))}
          </div>
        </Tooltip>
      ))}
    </div>
  );
}

ProgressBar4.propTypes = {
  classes: PropTypes.object.isRequired,
  counters: PropTypes.object.isRequired,
};

// ---------------------------------------------------------------------------
// FilterTile — 1 linha empilhada dentro do card de contagem
// ---------------------------------------------------------------------------
//
// Visual e' um "botaozinho" com pontinho colorido + label + contador a
// direita. Quando ``active``, ganha borda + barrinha lateral ciano.
// Quando ``readonly``, e' so leitura (caso do tile "Entregues" que nao
// tem filtro correspondente em ``handleBadgeClick``).

function FilterTile({
  classes,
  active,
  disabled,
  readOnly,
  dotKind,
  label,
  subLabel,
  count,
  onClick,
}) {
  const dotClassMap = {
    delivered: classes.tileDotDelivered,
    in_progress: classes.tileDotInProgress,
    attention: classes.tileDotAttention,
    pending_decision: classes.tileDotPendingDecision,
    critical: classes.tileDotCritical,
    not_sent: classes.tileDotNotSent,
  };
  return (
    <button
      type="button"
      className={cx(classes.tile, {
        active,
        disabled: disabled && !active,
        "read-only": readOnly,
      })}
      onClick={() => {
        if (disabled || readOnly) return;
        onClick();
      }}
      aria-pressed={active || undefined}
      disabled={disabled && !active}
    >
      <div className={cx(classes.tileDot, dotClassMap[dotKind])} />
      <span className={classes.tileLabel}>
        {label}
        {subLabel && <span className={classes.tileLabelMute}> {subLabel}</span>}
      </span>
      <span
        className={cx(classes.tileCount, { [classes.tileCountActive]: active })}
      >
        {count}
      </span>
    </button>
  );
}

FilterTile.propTypes = {
  classes: PropTypes.object.isRequired,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  dotKind: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  subLabel: PropTypes.string,
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func,
};

// ---------------------------------------------------------------------------
// CountCard — donut a esquerda + tiles a direita
// ---------------------------------------------------------------------------

function CountCard({
  classes,
  t,
  title,
  scope,
  counters,
  badgeFilter,
  onBadgeClick,
}) {
  // ``attention`` aqui e' o sinal OR-based vindo de ``computeCounters``
  // (qualquer substate em warning) — usado apenas no tooltip de
  // decomposicao do donut (delivered = clean + attention + in_progress).
  // O contador do TILE "Em atencao" vem do ``tally.buckets.attention``
  // abaixo (winner-take-all + ``in_progress_attention``).
  const { total, delivered, deliveredClean, attention, states } = counters;
  // Aglutinacao 9 -> 4 deriva da contagem CRUA (``sub``), nao dos
  // campos pre-agregados de ``counters``. Garante que a soma dos 4
  // tiles bata com o total renderizado na barra, independente de
  // mudancas em ``computeCounters``: aqui a fonte da verdade do
  // bucket continua sendo ``STATE_TO_BUCKET`` (ver topo do arquivo).
  const tally = tallyStates(states);
  const sub = tally.sub;
  const subCount = (s) => sub[s] || 0;
  const okCount = tally.buckets.delivered;
  const attentionCount = tally.buckets.attention;
  const criticalCount = tally.buckets.critical;
  const notSentCount = tally.buckets.not_sent;
  // Sub-componentes (para sublabels i18n). ``pending_decision`` ja
  // aparece no proprio label do tile ("critico / aguarda decisao") —
  // nao duplicamos a contagem como sublabel.
  const inProgress = subCount("in_progress");
  const processingCount = subCount("processing");
  const okSublabel =
    inProgress > 0
      ? t("inputs_v3.progress_band.tile_ok_provisional_sublabel", {
          count: inProgress,
        })
      : null;
  const processingSublabel =
    processingCount > 0
      ? t("inputs_v3.progress_band.tile_not_sent_processing_sublabel", {
          count: processingCount,
        })
      : null;

  // Tooltip do donut espelha o ``delivered_tooltip`` ja registrado no
  // i18n — composicao: limpos + atencao + aguardando avancar.
  const deliveredTooltip =
    delivered > 0
      ? t("inputs_v3.stats.delivered_tooltip", {
          clean: deliveredClean,
          attention,
          in_progress: inProgress,
        })
      : null;

  const isActive = (type) =>
    !!badgeFilter && badgeFilter.scope === scope && badgeFilter.type === type;

  return (
    <Card className={classes.innerCard}>
      <CardBody className={classes.innerBody}>
        <div className={classes.innerTitle}>{title}</div>
        <div className={classes.cardSplit}>
          <Tooltip
            title={deliveredTooltip || ""}
            disableHoverListener={!deliveredTooltip}
            arrow
            placement="top"
          >
            <div className={classes.donutHolder}>
              <DashedDonut states={states} size={92} strokeWidth={7} />
              <div className={classes.donutOverlay}>
                <div className={classes.donutNum}>
                  {delivered}
                  <span className={classes.donutDen}>/{total}</span>
                </div>
                <div className={classes.donutCap}>
                  {t("inputs_v3.stats.delivered")}
                </div>
              </div>
            </div>
          </Tooltip>

          <div className={classes.tileStack}>
            {/* 4 tiles aglutinando os 9 estados canonicos. A correspondencia
                ``estado -> tile`` vive em ``STATE_TO_BUCKET`` no topo do
                arquivo (mesma fonte usada pela barra para garantir 1:1). */}
            <FilterTile
              classes={classes}
              dotKind="delivered"
              label={t("inputs_v3.progress_band.tile_ok")}
              subLabel={okSublabel}
              count={okCount}
              active={isActive("delivered")}
              disabled={okCount === 0}
              onClick={() => onBadgeClick(scope, "delivered", okCount)}
            />
            <FilterTile
              classes={classes}
              dotKind="attention"
              label={t("inputs_v3.stats.attention_count", {
                count: attentionCount,
              }).replace(/^\d+\s*/, "")}
              count={attentionCount}
              active={isActive("attention")}
              disabled={attentionCount === 0}
              onClick={() => onBadgeClick(scope, "attention", attentionCount)}
            />
            <FilterTile
              classes={classes}
              dotKind="critical"
              label={t("inputs_v3.progress_band.tile_critical_or_decision")}
              count={criticalCount}
              active={isActive("critical")}
              disabled={criticalCount === 0}
              onClick={() => onBadgeClick(scope, "critical", criticalCount)}
            />
            <FilterTile
              classes={classes}
              dotKind="not_sent"
              label={t("inputs_v3.stats.not_sent_count", {
                count: notSentCount,
              }).replace(/^\d+\s*/, "")}
              subLabel={processingSublabel}
              count={notSentCount}
              active={isActive("not_sent")}
              disabled={notSentCount === 0}
              onClick={() => onBadgeClick(scope, "not_sent", notSentCount)}
            />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

CountCard.propTypes = {
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  counters: PropTypes.object.isRequired,
  badgeFilter: PropTypes.object,
  onBadgeClick: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// ResponsiblesCard — lista clicavel com checkbox como filtro
// ---------------------------------------------------------------------------

function ResponsiblesCard({
  classes,
  overlayScrollClasses,
  t,
  responsibleList,
  filteredResponsibleUsers,
  onToggleResponsible,
}) {
  const selectedIds = new Set(filteredResponsibleUsers.map((u) => u.id));
  // O ``overflow: overlay`` da scrollbar transparente fez o browser
  // perder o hit-test do wheel as vezes, propagando o evento para a
  // pagina inteira. Esse hook intercepta wheel com listener nativo
  // (passive:false) e aplica o scroll manualmente, segurando a
  // propagacao quando ha rolagem disponivel no proprio elemento.
  const respScrollRef = useNativeWheelScroll();
  return (
    <Card className={classes.innerCard}>
      <CardBody className={classes.innerBody}>
        <div className={classes.innerTitle}>
          {t("inputs_v3.stats.status_by_responsible")}
        </div>
        {responsibleList.length === 0 ? (
          <div className={classes.respEmpty}>
            {t("inputs_v3.stats.no_pending")}
          </div>
        ) : (
          <div
            ref={respScrollRef}
            className={cx(
              classes.respScroll,
              overlayScrollClasses.overlayScroll,
            )}
          >
            {responsibleList.map((item) => {
              const isSelected = selectedIds.has(item.id);
              return (
                <div
                  key={item.id}
                  className={cx(classes.respRow, { selected: isSelected })}
                  onClick={() => onToggleResponsible(item.id, item.name)}
                  role="button"
                  aria-pressed={isSelected || undefined}
                >
                  <div
                    className={cx(classes.respCheck, {
                      checked: isSelected,
                    })}
                  >
                    {isSelected && (
                      <svg width="10" height="10" viewBox="0 0 10 10">
                        <polyline
                          points="1.5,5.3 4,7.8 8.5,2.5"
                          fill="none"
                          stroke={whiteColor}
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div
                    className={cx(classes.respName, {
                      [classes.respNameEmpty]: !item.hasResponsible,
                      [classes.respNameMe]: item.isCurrentUser,
                    })}
                    title={item.name}
                  >
                    {item.name}
                  </div>
                  <div className={classes.respBadges}>
                    {item.delivered > 0 && (
                      <div className={classes.respTiny}>
                        <span
                          className={classes.respTinyDot}
                          style={{ background: successColor[2] }}
                        />
                        {item.delivered}
                      </div>
                    )}
                    {item.critical > 0 && (
                      <div className={classes.respTiny}>
                        <span
                          className={classes.respTinyDot}
                          style={{ background: dangerColor[2] }}
                        />
                        {item.critical}
                      </div>
                    )}
                    {item.attention > 0 && (
                      <div className={classes.respTiny}>
                        <span
                          className={classes.respTinyDot}
                          style={{ background: warningColor[1] }}
                        />
                        {item.attention}
                      </div>
                    )}
                    {item.notSent > 0 && (
                      <div className={classes.respTiny}>
                        <span
                          className={classes.respTinyDot}
                          style={{ background: grayColor[5] }}
                        />
                        {item.notSent}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

ResponsiblesCard.propTypes = {
  classes: PropTypes.object.isRequired,
  overlayScrollClasses: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  responsibleList: PropTypes.array.isRequired,
  filteredResponsibleUsers: PropTypes.array.isRequired,
  onToggleResponsible: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// ModelingProgressBandV3 — componente principal
// ---------------------------------------------------------------------------

export default function ModelingProgressBandV3({
  allCounters,
  mineCounters,
  responsibleList,
  badgeFilter,
  onBadgeClick,
  filteredResponsibleUsers,
  onToggleResponsible,
  monthLabel,
  embedded,
  transitionFaded,
}) {
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const classes = useBandStyles({ clientColor });
  const overlayScrollClasses = useOverlayScrollStyles();

  const [expanded, setExpanded] = useState(readInitialExpanded);
  useEffect(() => {
    persistExpanded(expanded);
  }, [expanded]);

  const total = allCounters?.total || 0;
  const delivered = allCounters?.delivered || 0;

  // Tooltip do contador X/Y: mesma decomposicao do donut, para o
  // usuario ler o numero do compacto sem precisar abrir o expandido.
  const counterTooltip =
    delivered > 0
      ? t("inputs_v3.stats.delivered_tooltip", {
          clean: allCounters.deliveredClean,
          attention: allCounters.attention,
          in_progress: allCounters.inProgress,
        })
      : "";

  const bottomLineFadeStyle = transitionFaded
    ? {
        opacity: 0.45,
        transition: "opacity 180ms ease",
        pointerEvents: "none",
      }
    : {
        opacity: 1,
        transition: "opacity 180ms ease",
      };

  const innerContent = (
    <>
      {/* ===== Compacto (sempre visivel) =====
          PA-022 (mai/2026): com a nova ``InputsV3GlobalTopBar`` no
          header global, o conteudo desta faixa foi reduzido para o
          essencial — mes, contador, barra colorida e chevron.
          Tudo numa linha unica.
          - O chip de status saiu daqui (vive na topbar global).
          - O texto contextual ``Lendo X.../Cruzando Y...`` saiu
            (vive na topbar global como complement da headline).
          - O botao ``Cálculos`` saiu (os tiles "Pendencias"/"Blocos"
            da topbar global ja abrem o drawer na aba certa).
          - O titulo "Progresso da modelagem" saiu (redundante). */}
      <div className={classes.compactWrap}>
        <div className={classes.compactLine}>
          {monthLabel && <span className={classes.monthTag}>{monthLabel}</span>}
          <Tooltip
            title={counterTooltip}
            disableHoverListener={!counterTooltip}
            arrow
            placement="top"
          >
            <span className={classes.counter} style={bottomLineFadeStyle}>
              {delivered}
              <small>/{total}</small>
            </span>
          </Tooltip>
          <div className={classes.compactBarWrap} style={bottomLineFadeStyle}>
            <ProgressBar4 classes={classes} counters={allCounters} />
          </div>
          {/* Rev. 2026-05-26: pill da legenda MIGRADO da barra de
              filtros (sort + abrir/fechar) para aqui — fica entre a
              barra colorida e o chevron de expand do progresso.
              Usa o componente compartilhado ``InputsLegendPill`` para
              que a visao detalhada e o Overview rendam pill +
              popover identicos. */}
          <div style={{ marginRight: "-4px" }}>
            <InputsLegendPill placement="bottom-end" />
          </div>
          <Tooltip
            title={t(
              expanded
                ? "inputs_v3.progress_band.collapse"
                : "inputs_v3.progress_band.expand",
            )}
          >
            <div
              className={cx(classes.chevronBtn, { open: expanded })}
              onClick={() => setExpanded((v) => !v)}
              role="button"
              aria-expanded={expanded}
            >
              <ExpandMore />
            </div>
          </Tooltip>
        </div>
      </div>

      {/* ===== Expandido (3 cards classicos dentro da mesma faixa) ===== */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={classes.expanded}>
          <GridContainer alignItems="stretch">
            <GridItem xs={4} style={{ display: "flex" }}>
              <div style={{ flex: 1 }}>
                <CountCard
                  classes={classes}
                  t={t}
                  title={t("inputs_v3.stats.scope_all")}
                  scope="all"
                  counters={allCounters}
                  badgeFilter={badgeFilter}
                  onBadgeClick={onBadgeClick}
                />
              </div>
            </GridItem>
            <GridItem xs={4} style={{ display: "flex" }}>
              <div style={{ flex: 1 }}>
                <CountCard
                  classes={classes}
                  t={t}
                  title={t("inputs_v3.stats.scope_mine")}
                  scope="mine"
                  counters={mineCounters}
                  badgeFilter={badgeFilter}
                  onBadgeClick={onBadgeClick}
                />
              </div>
            </GridItem>
            <GridItem xs={4} style={{ display: "flex" }}>
              <div style={{ flex: 1 }}>
                <ResponsiblesCard
                  classes={classes}
                  overlayScrollClasses={overlayScrollClasses}
                  t={t}
                  responsibleList={responsibleList}
                  filteredResponsibleUsers={filteredResponsibleUsers}
                  onToggleResponsible={onToggleResponsible}
                />
              </div>
            </GridItem>
          </GridContainer>
        </div>
      </Collapse>
    </>
  );

  return embedded ? (
    <div className={classes.bandEmbeddedWrap}>
      <div className={classes.bandBody}>{innerContent}</div>
    </div>
  ) : (
    <Card className={classes.bandCard}>
      <CardBody className={classes.bandBody}>{innerContent}</CardBody>
    </Card>
  );
}

ModelingProgressBandV3.propTypes = {
  allCounters: PropTypes.object.isRequired,
  mineCounters: PropTypes.object.isRequired,
  responsibleList: PropTypes.array.isRequired,
  badgeFilter: PropTypes.object,
  onBadgeClick: PropTypes.func.isRequired,
  filteredResponsibleUsers: PropTypes.array.isRequired,
  onToggleResponsible: PropTypes.func.isRequired,
  monthLabel: PropTypes.string,
  embedded: PropTypes.bool,
  transitionFaded: PropTypes.bool,
};

ModelingProgressBandV3.defaultProps = {
  badgeFilter: null,
  filteredResponsibleUsers: [],
  monthLabel: null,
  embedded: false,
  transitionFaded: false,
};
